import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";

import { useLoginResult } from "../State/LoginResultContext";
import { API_URL } from "../utils/constants";
import { useErrorRenderer } from "../Hooks/useErrorRenderer";
import { showToast } from "../utils/toast";

const options = ["General Feedback", "Technical Help"];
export default function ContactUS() {
  const [subject, setSubject] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [message, setMessage, isError, setIsError, resetErrorState] =
    useErrorRenderer();
  const { loginResults } = useLoginResult();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subject) {
      setMessage("Please choose a subject.");
      setIsError(true);
      resetErrorState();
      return;
    }
    if (!messageText.trim()) {
      setMessage("Please enter a message.");
      setIsError(true);
      resetErrorState();
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/contacts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${loginResults.token}`,
        },
        body: JSON.stringify({
          subject,
          message: messageText.trim(),
          organization_id: loginResults.activeOrganization.id,
        
        }),
      });

      const data = await response.json();
      if (response.status === 200) {
        showToast("Message sent. We'll get back to you soon.", { type: "success" });
        navigate("/my-events");
      } else {
        setMessage(data.message);
        setIsError(true);
        resetErrorState();
      }
    } catch (error) {
      setMessage(error.message);
      setIsError(true);
      resetErrorState();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col">
      <div className="flex flex-1 flex-col items-center px-6 py-10 sm:py-14">
        <div className="w-full sm:mx-auto sm:w-full sm:max-w-xl">
          <h2 className="text-center text-[32px] sm:text-[40px] font-bold tracking-tight text-white atkinson-font">
            Contact Us
          </h2>
        </div>

        <div className="mt-8 w-full sm:mx-auto sm:w-full sm:max-w-xl">
          <form
            onSubmit={handleSubmit}
            method="POST"
            className="space-y-6 mx-2 sm:mx-4"
          >
            <div>
              <label className="block text-2xl font-bold text-white text-left atkinson-font">
                Subject<span className="text-red-400">*</span>
              </label>
              <div className="relative mt-2">
                <Listbox value={subject} onChange={setSubject}>
                  <ListboxButton className="relative block w-full rounded-md border border-[#FFFFFFCC] py-3 pl-3 pr-10 !bg-[#595959] hover:!bg-[#595959] active:!bg-[#595959] text-[#FFFFFF] text-base text-left placeholder:text-[#FFFFFFCC] focus:border-[#FFFFFF]">
                    <span
                      className={`block truncate tracking-tight ${
                        subject ? "text-white" : "text-white/70"
                      }`}
                    >
                      {subject ?? "Choose the subject"}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronUpDownIcon
                        aria-hidden="true"
                        className="h-5 w-5 text-white/70"
                      />
                    </span>
                  </ListboxButton>

                  <ListboxOptions
                    transition
                    className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-lg bg-white p-2 text-base shadow-lg ring-1 ring-black/10 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                  >
                    {options.map((item) => (
                      <ListboxOption
                        key={item}
                        value={item}
                        className="group relative cursor-pointer select-none p-3 text-gray-900 data-[focus]:bg-[#28094C] data-[focus]:text-white rounded-lg"
                      >
                        <span className="block truncate tracking-tight">
                          {item}
                        </span>

                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#28094C] group-data-[focus]:text-white [.group:not([data-selected])_&]:hidden">
                          <CheckIcon aria-hidden="true" className="h-5 w-5" />
                        </span>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </Listbox>
              </div>
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-2xl font-bold text-white text-left atkinson-font"
              >
                Message<span className="text-red-400">*</span>
              </label>
              <div className="mt-2">
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  maxLength={2000}
                  placeholder="What can we help you with?"
                  className="block w-full rounded-md border border-[#FFFFFFCC] py-3 px-3 !bg-[#595959] text-[#FFFFFF] text-base placeholder:text-[#FFFFFFCC] focus:border-[#FFFFFF] resize-y"
                  onChange={(e) => setMessageText(e.target.value)}
                  value={messageText}
                />
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={
                  isSubmitting || !subject || messageText.trim().length === 0
                }
                className="flex w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3]"
                style={{ border: "2px solid #6A6680" }}
              >
                {isSubmitting ? "Sending..." : "Send"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
